import React, { useRef } from 'react';
import { Box, Flex, SimpleGrid } from '@chakra-ui/react';
import { Context } from '../App';
import { SPACING } from './config';
import Divider from '../base-components/Divider';
import { GetBasicModelWState } from '@/base-components/hooks/useModel';
import ModelInput from '@/base-components/ModelInput';
import { UseQueryResult } from 'react-query';
import { debounce, isEqual } from 'lodash';
import { DropdownItem } from '@/api/base/dropdown-item';
import Dropdown from '@/base-components/Dropdown';
import Input from '@/base-components/Input';
import { Translations } from './Translations';

export type DropdownFilterType = {
    placeholder?: string,
    kind: "dropdown",
    items: (translations: Translations) => DropdownItem[],
    value: DropdownItem | null,
    title: (translations: Translations) => string,
};

export type DateFilterType = {
    kind: "date",
    value: string,
    title: (translations: Translations) => string,
};

export type FilterExtraType = DropdownFilterType | DateFilterType;

export type SearcherFilters<T extends Record<string, FilterExtraType>> = {
    keywords: string,
} & T;

const SearcherNew = <T extends Record<string, FilterExtraType>>({
    filters,
    setFilters,
    query,
    children,
}: {
    filters: SearcherFilters<T>,
    setFilters: (filters: SearcherFilters<T>) => void,
    query: UseQueryResult<any, unknown>,
    children?: React.ReactNode,
    title: string,
    description: string,
}) => {
    const { translations } = React.useContext(Context);
    const initialFilters = React.useRef<SearcherFilters<T>>(filters);
    const model = GetBasicModelWState<SearcherFilters<T>>(initialFilters.current);
    const lastValue = useRef<SearcherFilters<T>>(model.object);

    const debouncedRef = useRef<ReturnType<typeof debounce>>();
    if (!debouncedRef.current) {
        debouncedRef.current = debounce(() => {
            query.refetch();
        }, 250);
    }

    if (!isEqual(model.object, lastValue.current)) {
        debouncedRef.current();
        setFilters(model.object);
        lastValue.current = model.object;
    }

    return (
        <Box>
            <Flex gap={SPACING}>
                <Box flexGrow={1}>
                    <ModelInput title={null} model={model} fieldKey="keywords" />
                </Box>
                {children}
            </Flex>
            <SimpleGrid columns={5} gap={SPACING}>
                {Object.keys(model.object).filter(key => key !== 'keywords').map((key) => {
                    const f = (model.object as Record<string, FilterExtraType>)[key];
                    if (f.kind === 'dropdown') {
                        return <Dropdown key={key} placeholder={f.placeholder ?? translations.common.All} title={f.title(translations)} items={f.items(translations)} value={f.value} onChange={(value) => model.setObject({ ...model.object, [key]: { ...f, value } })} />;
                    }
                    if (f.kind === 'date') {
                        return <Input key={key} type="date" title={f.title(translations)} value={f.value} onChange={(e) => model.setObject({ ...model.object, [key]: { ...f, value: e.target.value } })} />;
                    }
                    return null;
                })}
            </SimpleGrid>
            <Divider />
        </Box>
    );
};

export default SearcherNew;
